import { useCallback, useEffect, useRef, useState } from 'react';

import { type OAuthPopupHandle, openOAuthPopup } from './openOAuthPopup';
import { type PopupSize } from './popupWindow';

type UseOAuthPopupParams<T> = {
  validate: (data: unknown) => data is T;
  allowedOrigin?: string;
  size?: PopupSize;
};

type OpenParams = {
  getUrl: () => Promise<string>;
};

export function useOAuthPopup<T>({
  validate,
  allowedOrigin = globalThis.location.origin,
  size,
}: UseOAuthPopupParams<T>) {
  const [isWaiting, setIsWaiting] = useState(false);
  const handleRef = useRef<OAuthPopupHandle<T> | null>(null);

  useEffect(() => () => handleRef.current?.close(), []);

  const open = useCallback(
    async ({ getUrl }: OpenParams): Promise<T | null> => {
      handleRef.current?.close();

      const handle = openOAuthPopup<T>({
        getUrl,
        validate,
        allowedOrigin,
        size,
      });

      handleRef.current = handle;
      setIsWaiting(true);

      try {
        return await handle.result;
      } finally {
        if (handleRef.current === handle) {
          handleRef.current = null;
          setIsWaiting(false);
        }
      }
    },
    [validate, allowedOrigin, size]
  );

  return { open, isWaiting };
}
